import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  Archive,
  Business,
  Close,
  Delete,
  Edit,
  Notes as NotesIcon,
  Person,
  Unarchive,
} from "@mui/icons-material"
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useDeleteClientMutation,
  useGetClientByIdQuery,
  useUpdateClientMutation,
} from "../../apis/appApi"
import { AddressSection } from "./AddressSection"
import { AuthorizedRecipientSection } from "./AuthorizedRecipientSection"
import { ClientOrdersSection } from "./ClientOrdersSection"
import { ClientTagSection } from "./ClientTagSection"
import { PhoneNumberSection } from "./PhoneNumberSection"
import { getAvatarColor, getInitials } from "./utils"

type CustomerDetailDrawerProps = {
  clientId: number | null
  open: boolean
  onClose: () => void
  onEdit: (clientId: number) => void
  onSuccess: (message: string) => void
}

const InfoRow: FC<{ label: string; value: React.ReactNode; "data-testid"?: string }> = ({
  label,
  value,
  "data-testid": testId,
}) => (
  <Stack direction="row" spacing={1} data-testid={testId}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value ?? "\u2014"}</Typography>
  </Stack>
)

const SectionHeader: FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
    <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle2" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "\u2014"
  return dayjs(raw).format("MMM D, YYYY")
}

function formatLanguage(lang: string | null | undefined, t: (key: string) => string): string {
  if (!lang) return "\u2014"
  const key = `clients.languageValue.${lang.toLowerCase()}`
  const translated = t(key)
  // If translation key not found, return raw value
  return translated === key ? lang.toUpperCase() : translated
}

function getClientType(client: { organisation?: string | null }): "business" | "individual" {
  return client.organisation ? "business" : "individual"
}

export const CustomerDetailDrawer: FC<CustomerDetailDrawerProps> = ({
  clientId,
  open,
  onClose,
  onEdit,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const { data: response, isLoading } = useGetClientByIdQuery(clientId!, {
    skip: !clientId,
  })
  const [updateClient] = useUpdateClientMutation()
  const [deleteClient] = useDeleteClientMutation()

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const client = response?.data

  const handleArchiveToggle = useCallback(async () => {
    if (!client) return
    try {
      await updateClient({
        id: client.id,
        body: { archived: !client.archived },
      }).unwrap()
      onSuccess(
        client.archived ? t("clients.unarchiveSuccess") : t("clients.archiveSuccess"),
      )
    } catch {
      // Error handled by RTK Query
    }
  }, [client, updateClient, onSuccess, t])

  const handleDelete = useCallback(async () => {
    if (!client) return
    try {
      await deleteClient(client.id).unwrap()
      setDeleteConfirmOpen(false)
      onSuccess(t("clients.deleteSuccess"))
      onClose()
    } catch {
      // Error handled by RTK Query
    }
  }, [client, deleteClient, onClose, onSuccess, t])

  const fullName = client ? `${client.firstName} ${client.lastName}` : ""

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        data-testid="customer-detail-drawer"
        slotProps={{ paper: { ...uiPaper("UI-DLV2-083"), sx: { width: 550, maxWidth: "100vw", display: "flex", flexDirection: "column" } } }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            {client && (
              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  fontSize: 20,
                  fontWeight: 600,
                  bgcolor: getAvatarColor(fullName),
                }}
              >
                {getInitials(client.firstName, client.lastName)}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" noWrap>
                  {client ? fullName : t("loading")}
                </Typography>
                {client && (
                  <>
                    <Chip
                      label={client.archived ? t("clients.archived") : t("clients.active")}
                      size="small"
                      data-testid="customer-detail-status"
                      sx={
                        client.archived
                          ? { bgcolor: "#f5f5f5", color: "#757575" }
                          : { bgcolor: "#e8f5e9", color: "#2e7d32" }
                      }
                    />
                    <Tooltip title={t(`clients.type.${getClientType(client)}Tooltip`)}>
                      <Chip
                        icon={getClientType(client) === "business" ? <Business sx={{ fontSize: 16 }} /> : <Person sx={{ fontSize: 16 }} />}
                        label={t(`clients.type.${getClientType(client)}`)}
                        size="small"
                        data-testid="customer-detail-type"
                        variant="outlined"
                        sx={{ borderColor: getClientType(client) === "business" ? "#1976d2" : "#9e9e9e", color: getClientType(client) === "business" ? "#1976d2" : "#616161" }}
                      />
                    </Tooltip>
                  </>
                )}
              </Stack>
              {client?.organisation && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {client.organisation}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {client && (
              <IconButton
                size="small"
                data-testid="customer-edit-btn"
                onClick={() => onEdit(client.id)}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            <IconButton onClick={onClose} size="small" data-testid="customer-close-btn">
              <Close />
            </IconButton>
          </Stack>
        </Stack>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {client && !isLoading && (
          <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                data-testid="customer-footer-edit-btn"
                onClick={() => onEdit(client.id)}
              >
                {t("edit")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={client.archived ? <Unarchive /> : <Archive />}
                data-testid="customer-archive-btn"
                onClick={handleArchiveToggle}
              >
                {client.archived ? t("clients.unarchive") : t("clients.archive")}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<Delete />}
                data-testid="customer-delete-btn"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                {t("delete")}
              </Button>
            </Stack>

            {/* Client Info */}
            <Paper {...uiSurface("UI-DLV2-084")} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <SectionHeader icon={<Person fontSize="small" />} label={t("clients.detail.info")} />
              <Stack spacing={1.5}>
                <InfoRow label={t("clients.firstName")} value={client.firstName} data-testid="customer-detail-firstName" />
                <InfoRow label={t("clients.lastName")} value={client.lastName} data-testid="customer-detail-lastName" />
                <InfoRow label={t("clients.email")} value={client.email} data-testid="customer-detail-email" />
                <InfoRow label={t("clients.organisation")} value={client.organisation} data-testid="customer-detail-organisation" />
                <InfoRow label={t("clients.language")} value={formatLanguage(client.language, t)} data-testid="customer-detail-language" />
                <InfoRow label={t("clients.externalReference")} value={client.externalReference} data-testid="customer-detail-externalReference" />
                <InfoRow
                  label={t("clients.includesInSurvey")}
                  value={client.includesInSurvey ? t("yes") : t("no")}
                  data-testid="customer-detail-includesInSurvey"
                />
                <InfoRow
                  label={t("clients.includesInTakeInChargeNotifications")}
                  value={client.includesInTakeInChargeNotifications ? t("yes") : t("no")}
                  data-testid="customer-detail-includesInTakeInCharge"
                />
                <InfoRow label={t("clients.dateCreated")} value={formatDate(client.dateCreated)} data-testid="customer-detail-dateCreated" />
                <InfoRow label={t("clients.lastUpdated")} value={formatDate(client.lastUpdated)} data-testid="customer-detail-lastUpdated" />
              </Stack>
            </Paper>

            {/* Notes */}
            <Paper {...uiSurface("UI-DLV2-085")} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <SectionHeader icon={<NotesIcon fontSize="small" />} label={t("clients.detail.notes")} />
              <Typography variant="body2" data-testid="customer-detail-notes" color={client.notes ? undefined : "text.disabled"}>
                {client.notes || t("clients.detail.noNotes")}
              </Typography>
            </Paper>

            {/* Phone Numbers — inline CRUD */}
            <Box sx={{ mb: 2 }}>
              <PhoneNumberSection
                clientId={client.id}
                phoneNumbers={client.phoneNumbers || []}
                onSuccess={onSuccess}
              />
            </Box>

            {/* Addresses — inline CRUD */}
            <Box sx={{ mb: 2 }}>
              <AddressSection
                clientId={client.id}
                addresses={client.addresses || []}
                onSuccess={onSuccess}
              />
            </Box>

            {/* Authorized Recipients — inline CRUD */}
            <Box sx={{ mb: 2 }}>
              <AuthorizedRecipientSection
                clientId={client.id}
                recipients={client.recipients || []}
                onSuccess={onSuccess}
              />
            </Box>

            {/* Tags — inline management */}
            <ClientTagSection
              clientId={client.id}
              clientTags={client.tags || []}
              onSuccess={onSuccess}
            />

            {/* Orders — client order history */}
            <Box sx={{ mt: 2 }}>
              <ClientOrdersSection
                clientId={client.id}
                onClose={onClose}
              />
            </Box>
          </Box>
        )}

      </Drawer>

      {/* Delete confirmation dialog */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-094") }}
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        data-testid="customer-delete-dialog"
      >
        <DialogTitle>{t("clients.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("clients.deleteConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} data-testid="customer-delete-cancel-btn">
            {t("cancel")}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" data-testid="customer-delete-confirm-btn">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
